import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { MirrorService } from 'services/MirrorService';

import { setupInterceptors } from 'api/httpInterceptors';
import { TrackingApi } from 'api/TrackingApi';
import { getScreenOrientation, getScreenSizes } from 'helpers/device';
import { useApiBrowserBackButton } from 'hooks/useApiBrowserBackButton';
import { useAppMeta } from 'hooks/useAppMeta';
import { useAuthenticateUser } from 'hooks/useAuthenticateUser';
import { useBoostExpire } from 'hooks/useBoostExpire';
import { useClickIdHack } from 'hooks/useClickIdHack';
import { useHistoryChange } from 'hooks/useHistoryChange';
import { useIdleAutoredirect } from 'hooks/useIdleAutoredirect';
import { useInitializeApp } from 'hooks/useInitializeApp';
import { useInterstitialPopup } from 'hooks/useInterstitialPopup';
import { useLandingInit } from 'hooks/useLandingInit';
import { useLandingPopunder } from 'hooks/useLandingPopunder';
import { useMatchPopup } from 'hooks/useMatchPopup';
import { useMirrorThemeColors } from 'hooks/useMirrorTheme';
import { useMultiAdsPopups } from 'hooks/useMultiAdsPopups';
import { usePageView } from 'hooks/usePageView';
import { usePromoteVipPopup } from 'hooks/usePromoteVipPopup';
import { useRequestPushPermissions } from 'hooks/useRequestPushPermissions';
import { useRetentionBrowserBackButton } from 'hooks/useRetentionBrowserBackButton';
import { useRetentionFunnels } from 'hooks/useRetentionFunnels';
import { useStopExitPopup } from 'hooks/useStopExitPopup';
import { useToast } from 'hooks/useToast';
import { useWebPushRetry } from 'hooks/useWebPushRetry';
import { useXPrevUrl } from 'hooks/useXPrevUrl';
import { useXRefererUrl } from 'hooks/useXRefererUrl';
import { getAuthenticated } from 'store/auth/selectors';
import { setPrevPageHref } from 'store/common/commonSlice';
import { getActiveSystemPopupSelector } from 'store/systemPopup/selectors';

import { RegistrationQuiz } from 'components/sections/Funnels/RegistrationQuiz';
import { AdBlockDetector } from 'components/shared/AdBlockDetector';
import { FullscreenSpinner } from 'components/shared/FullscreenSpinner';
import { MidsFooter } from 'components/shared/MidsFooter/MidsFooter';
import { useLimitTimeOfferPopup } from 'components/shared/Popups/LimitTimeOfferPopup/hooks/useLimitTimeOfferPopup';
import { useUploadPhotoPopup } from 'components/shared/Popups/UploadPhotoPopup/hooks/useUploadPhotoPopup';
import { SystemPopup } from 'components/shared/SystemPopup';
import { TransferUserPopup } from 'components/shared/TransferUserPopup';

import { Routes } from './Routes';

const EMAIL_CONFIRM_PARTNER_LOGIN_ID = '21';

export const SiteApp = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { pathname, search } = useLocation();

  const isAuthenticated = useSelector(getAuthenticated);
  const activeSystemPopupType = useSelector(getActiveSystemPopupSelector);

  const { showSuccessToast } = useToast();

  const { isInitialized } = useInitializeApp();
  const { tryAuthenticateUser } = useAuthenticateUser();

  const { isLandingInitLoading, isLandingInitLoaded } =
    useLandingInit(isInitialized);

  useLandingPopunder(isLandingInitLoaded);

  useClickIdHack();
  useXPrevUrl();
  useXRefererUrl();
  usePageView(!!isAuthenticated);

  useBoostExpire();
  useAppMeta();
  useMirrorThemeColors();
  useRequestPushPermissions({ isAuthenticated });
  useIdleAutoredirect();
  useRetentionFunnels();

  // popups
  useMultiAdsPopups();
  usePromoteVipPopup();
  useInterstitialPopup();
  useLimitTimeOfferPopup();
  useUploadPhotoPopup();
  useStopExitPopup();
  useMatchPopup();

  useWebPushRetry();
  useApiBrowserBackButton();
  useRetentionBrowserBackButton();

  useEffect(() => {
    return () => {
      dispatch(
        setPrevPageHref(`${document.location.origin}${pathname}${search}`)
      );
    };
  }, [dispatch, pathname, search]);

  useEffect(() => {
    setupInterceptors({ dispatch, history });
  }, [dispatch, history]);

  useEffect(() => {
    if (isInitialized) {
      tryAuthenticateUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  useEffect(() => {
    return history.listen(() => {
      window.scrollTo(0, 0);
    });
  }, [history]);

  useEffect(() => {
    const handleDeviceRotation = () => {
      TrackingApi.trackScreenOrientation({
        screen: {
          orientation:
            getScreenOrientation() === 'portrait' ? 'landscape' : 'portrait',
          ...getScreenSizes(),
        },
      });
    };

    window.addEventListener('orientationchange', handleDeviceRotation);

    return () =>
      window.removeEventListener('orientationchange', handleDeviceRotation);
  }, []);

  useEffect(() => {
    if (!MirrorService.isSimpleDates) {
      document.body.classList.add('grey-bg');
    }

    return () => {
      if (!MirrorService.isSimpleDates) {
        document.body.classList.remove('grey-bg');
      }
    };
  }, []);

  useHistoryChange({
    onFirstPageLoad() {
      if (
        queryString.parse(search).partner_login_id ===
        EMAIL_CONFIRM_PARTNER_LOGIN_ID
      ) {
        showSuccessToast({
          title: 'Congratulations!',
          subtitle: 'Your email is confirmed',
        });
      }
    },
  });

  useEffect(() => {
    if (isInitialized) TrackingApi.trackAppLoadEnd();
  }, [isInitialized]);

  if (!isInitialized || isLandingInitLoading) {
    return <FullscreenSpinner />;
  }

  return (
    <>
      {(MirrorService.isHookupers ||
        MirrorService.isMePlusYouOrLocalsjoy ||
        MirrorService.isLocalsDateOrLustStation) && <TransferUserPopup />}

      {activeSystemPopupType && <SystemPopup />}

      {MirrorService.isSimpleDates && <RegistrationQuiz />}

      {isAuthenticated !== null && <Routes />}

      <AdBlockDetector />

      {MirrorService.isMidsFooterShow && <MidsFooter />}
    </>
  );
};
